import { useState, useEffect, useCallback, useRef } from "react";
import { settingsApi } from "@/services/api";

// ── In-memory cache to prevent duplicate API calls across hook instances ──────
interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

let companyCache: CacheEntry<CompanySettings> | null = null;
let siteCache: CacheEntry<SiteSettings> | null = null;
let moduleCache: CacheEntry<ModuleSettings> | null = null;

// Pending promise deduplication — prevents multiple simultaneous fetches
let companyFetchPromise: Promise<CompanySettings> | null = null;
let siteFetchPromise: Promise<SiteSettings> | null = null;
let moduleFetchPromise: Promise<ModuleSettings> | null = null;

// ── URL resolver ─────────────────────────────────────────────────────────────
// The backend stores logo/avatar URLs using its own BACKEND_URL env var which
// may differ from the live domain. We always rewrite /uploads/* paths to point
// at the correct backend origin derived from VITE_API_URL.
const API_ORIGIN = (() => {
  const raw = import.meta.env.VITE_API_URL as string | undefined;
  if (!raw) return '';
  try {
    // Strip trailing /api (or /api/) to get the bare origin
    const url = new URL(raw);
    return url.origin; // e.g. https://jobcard.icongroup.net.in
  } catch {
    return '';
  }
})();

/**
 * Rewrites any /uploads/* URL so it always points at the correct backend origin.
 * Also fixes old /uploads/branding/ paths to /uploads/ (branding subfolder removed).
 * If the stored URL already has the right origin, it is returned unchanged.
 * If it is a relative path it is prefixed with the API origin.
 * If it is an absolute URL with a different origin (e.g. localhost), the path
 * is extracted and re-prefixed with the correct origin.
 * Returns empty string for data URIs, blob URLs, or invalid URLs to prevent
 * ERR_NAME_NOT_RESOLVED errors from bad stored hostnames.
 */
export function resolveUploadUrl(url: string | undefined | null): string {
  if (!url) return '';
  // Pass through data URIs and blob URLs as-is (no network request needed)
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (!API_ORIGIN) return url;
  try {
    // Fix old /uploads/branding/ paths to /uploads/ (legacy fix)
    let fixedUrl = url.replace('/uploads/branding/', '/uploads/');
    
    // Relative path — just prefix with API origin
    if (fixedUrl.startsWith('/')) return `${API_ORIGIN}${fixedUrl}`;
    const parsed = new URL(fixedUrl);
    // Already correct origin — return as-is
    if (parsed.origin === API_ORIGIN) return fixedUrl;
    // Only rewrite /uploads/* paths — don't touch external CDN URLs
    if (parsed.pathname.startsWith('/uploads/')) {
      return `${API_ORIGIN}${parsed.pathname}${parsed.search}`;
    }
    // For any other path on a different origin (e.g. localhost), rewrite to correct origin
    if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
      return `${API_ORIGIN}${parsed.pathname}${parsed.search}`;
    }
    return fixedUrl;
  } catch {
    return url;
  }
}

// ── Company Settings ──────────────────────────────────────────────────────────

export interface CompanySettings {
  name: string;
  logo: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  gstin: string;
  currency: string;
  country: string;
  state: string;
  timezone: string;
  invoicePrefix: string;
  jobCardPrefix: string;
  branchPrefix: string;
}

// Empty defaults — real values always come from the DB
export const DEFAULT_SETTINGS: CompanySettings = {
  name: "",
  logo: "",
  address: "",
  email: "",
  phone: "",
  website: "",
  gstin: "",
  currency: "INR",
  country: "",
  state: "",
  timezone: "",
  invoicePrefix: "INV",
  jobCardPrefix: "SURV",
  branchPrefix: "HYD",
};

/**
 * Fetches company settings from the API (MariaDB via backend).
 * Uses in-memory cache to prevent duplicate API calls across hook instances.
 */
export function useCompanySettings() {
  const [settings, setSettings] = useState<CompanySettings>(
    companyCache ? companyCache.data : DEFAULT_SETTINGS
  );
  const [loading, setLoading] = useState(!companyCache);
  const mountedRef = useRef(true);

  const fetchSettings = useCallback(async (force = false) => {
    // Use cache if fresh and not forced
    if (!force && companyCache && Date.now() - companyCache.fetchedAt < CACHE_TTL_MS) {
      setSettings(companyCache.data);
      setLoading(false);
      return;
    }
    setLoading(true);
    // Deduplicate concurrent fetches
    if (!companyFetchPromise) {
      companyFetchPromise = settingsApi.getCompanyProfile()
        .then((data: Record<string, string>) => {
          // Map both snake_case and camelCase keys from backend
          const mapped: Partial<CompanySettings> = {
            name: data.company_name || data.name || DEFAULT_SETTINGS.name,
            email: data.company_email || data.email || DEFAULT_SETTINGS.email,
            phone: data.company_phone || data.phone || DEFAULT_SETTINGS.phone,
            gstin: data.company_gstin || data.gstin || DEFAULT_SETTINGS.gstin,
            website: data.company_website || data.website || DEFAULT_SETTINGS.website,
            address: data.company_address || data.address || DEFAULT_SETTINGS.address,
            invoicePrefix: data.invoice_prefix || data.invoicePrefix || DEFAULT_SETTINGS.invoicePrefix,
            jobCardPrefix: data.job_card_prefix || data.jobCardPrefix || DEFAULT_SETTINGS.jobCardPrefix,
            branchPrefix: data.branch_prefix || data.branchPrefix || DEFAULT_SETTINGS.branchPrefix,
          };
          const merged = { ...DEFAULT_SETTINGS, ...mapped };
          companyCache = { data: merged, fetchedAt: Date.now() };
          companyFetchPromise = null;
          return merged;
        })
        .catch(() => {
          companyFetchPromise = null;
          return companyCache?.data ?? DEFAULT_SETTINGS;
        });
    }
    try {
      const result = await companyFetchPromise;
      if (mountedRef.current) setSettings(result);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchSettings();
    return () => { mountedRef.current = false; };
  }, [fetchSettings]);

  // Listen for in-session updates dispatched by the Settings page save
  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<CompanySettings>;
      setSettings((prev) => ({ ...prev, ...custom.detail }));
    };
    window.addEventListener("company-settings-updated", handler);
    return () => window.removeEventListener("company-settings-updated", handler);
  }, []);

  /**
   * saveSettings — updates local state, cache, and notifies other components.
   * The actual API call is made by the Settings page directly.
   */
  const saveSettings = useCallback((updated: CompanySettings) => {
    companyCache = { data: updated, fetchedAt: Date.now() };
    setSettings(updated);
    window.dispatchEvent(new CustomEvent("company-settings-updated", { detail: updated }));
  }, []);

  return { settings, saveSettings, loading, refetch: () => fetchSettings(true) };
}

/**
 * Synchronous helper that returns defaults.
 * Use only where a hook cannot be called (e.g. outside React tree).
 * Prefer useCompanySettings() inside components.
 */
export function getCompanySettings(): CompanySettings {
  return DEFAULT_SETTINGS;
}

// ── Site Settings ─────────────────────────────────────────────────────────────

export interface SiteSettings {
  site_logo_url: string;
  site_favicon_url: string;
  site_title: string;
  site_tagline: string;
  site_primary_color: string;
  site_date_format: string;
  site_sidebar_style: string;
}

export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  site_logo_url: "",
  site_favicon_url: "",
  site_title: "SurveyPro ERP",
  site_tagline: "Survey & CAD Management System",
  site_primary_color: "#7c3aed",
  site_date_format: "DD/MM/YYYY",
  site_sidebar_style: "dark",
};

/**
 * Fetches site/branding settings from the API (MariaDB via backend).
 * Uses in-memory cache to prevent duplicate API calls across hook instances.
 */
export function useSiteSettings() {
  const [siteSettings, setSiteSettings] = useState<SiteSettings>(
    siteCache ? siteCache.data : DEFAULT_SITE_SETTINGS
  );
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // Use cache if fresh
    if (siteCache && Date.now() - siteCache.fetchedAt < CACHE_TTL_MS) {
      setSiteSettings(siteCache.data);
      return () => { mountedRef.current = false; };
    }
    // Deduplicate concurrent fetches
    if (!siteFetchPromise) {
      siteFetchPromise = settingsApi.getSiteSettings()
        .then((data: Partial<SiteSettings>) => {
          const merged = { ...DEFAULT_SITE_SETTINGS, ...(data && typeof data === 'object' ? data : {}) };
          siteCache = { data: merged, fetchedAt: Date.now() };
          siteFetchPromise = null;
          return merged;
        })
        .catch(() => {
          siteFetchPromise = null;
          return siteCache?.data ?? DEFAULT_SITE_SETTINGS;
        });
    }
    siteFetchPromise.then((result) => {
      if (mountedRef.current) setSiteSettings(result);
    });
    return () => { mountedRef.current = false; };
  }, []);

  // Listen for in-session updates dispatched by SiteSettingsTab after save
  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<SiteSettings>;
      const updated = { ...DEFAULT_SITE_SETTINGS, ...custom.detail };
      siteCache = { data: updated, fetchedAt: Date.now() };
      setSiteSettings(updated);
    };
    window.addEventListener("site-settings-updated", handler);
    return () => window.removeEventListener("site-settings-updated", handler);
  }, []);

  return siteSettings;
}

/**
 * Synchronous helper — returns defaults only.
 * Prefer useSiteSettings() inside components.
 */
export function getSiteSettings(): SiteSettings {
  return DEFAULT_SITE_SETTINGS;
}

// ── Module Settings ───────────────────────────────────────────────────────────

export interface ModuleSettings {
  module_invoices: boolean;
  module_payments: boolean;
  module_quotations: boolean;
  module_field_tracking: boolean;
  module_deliverables: boolean;
  module_client_portal: boolean;
  module_reports: boolean;
}

export const DEFAULT_MODULE_SETTINGS: ModuleSettings = {
  module_invoices: true,
  module_payments: true,
  module_quotations: true,
  module_field_tracking: true,
  module_deliverables: true,
  module_client_portal: true,
  module_reports: true,
};

export function useModuleSettings() {
  const [modules, setModules] = useState<ModuleSettings>(
    moduleCache ? moduleCache.data : DEFAULT_MODULE_SETTINGS
  );
  const [loading, setLoading] = useState(!moduleCache);
  const mountedRef = useRef(true);

  const fetchModules = useCallback(async (force = false) => {
    // Use cache if fresh and not forced
    if (!force && moduleCache && Date.now() - moduleCache.fetchedAt < CACHE_TTL_MS) {
      setModules(moduleCache.data);
      setLoading(false);
      return;
    }
    setLoading(true);
    // Deduplicate concurrent fetches
    if (!moduleFetchPromise) {
      moduleFetchPromise = settingsApi.getModuleSettings()
        .then((data: Partial<ModuleSettings>) => {
          const merged = { ...DEFAULT_MODULE_SETTINGS, ...(data && typeof data === 'object' ? data : {}) };
          moduleCache = { data: merged, fetchedAt: Date.now() };
          moduleFetchPromise = null;
          return merged;
        })
        .catch(() => {
          moduleFetchPromise = null;
          return moduleCache?.data ?? DEFAULT_MODULE_SETTINGS;
        });
    }
    try {
      const result = await moduleFetchPromise;
      if (mountedRef.current) setModules(result);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchModules();
    return () => { mountedRef.current = false; };
  }, [fetchModules]);

  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<ModuleSettings>;
      setModules((prev) => ({ ...prev, ...custom.detail }));
    };
    window.addEventListener('module-settings-updated', handler);
    return () => window.removeEventListener('module-settings-updated', handler);
  }, []);

  const saveModules = useCallback((updated: ModuleSettings) => {
    moduleCache = { data: updated, fetchedAt: Date.now() };
    setModules(updated);
    window.dispatchEvent(new CustomEvent('module-settings-updated', { detail: updated }));
  }, []);

  return { modules, saveModules, loading, refetch: () => fetchModules(true) };
}
